import { blue } from '@ant-design/colors'
import React, { FC, useCallback, useContext, useMemo, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { Colors } from '../../../../../common/constants/colors'
import { DialogBody } from '../../../../dialog/subcomponents/dialog-body/DialogBody'
import { DialogFooterButton } from '../../../../dialog/subcomponents/dialog-footer-button/DialogFooterButton'
import { DialogFooter } from '../../../../dialog/subcomponents/dialog-footer/DialogFooter'
import { DialogHeader } from '../../../../dialog/subcomponents/dialog-header/DialogHeader'
import { FormRow } from '../../../../form-row/FormRow'
import { CompleteProfileDialogContext } from '../../complete-profile-dialog-context'
import PhoneInput, { formatPhoneNumber } from "react-phone-number-input";
import { Select } from '../../../../select/Select'
import { countries as sourceCountries, Country as SourceCountry } from "countries-list";
import PhoneNumber from "awesome-phonenumber";
import { useForm, Controller } from "react-hook-form";
import { Student } from '../../../../../common/types/types'
import { useCurrentUserProfile } from '../../../../../common/hooks/database/use-current-user-profile'
import { createStudent } from '../../../../../common/logic/student-logic'
import { Spinner } from '@blueprintjs/core'
import { LoadingOverlay } from '../../../../loading-overlay/LoadingOverlay'
import { grades } from '../../../../../common/constants/grades'

export type Country = SourceCountry & { id: keyof typeof sourceCountries }

type ProvideStudentDetailsProps = {}

export const ProvideStudentDetails: FC<ProvideStudentDetailsProps> = () => {

	// hooks
	const styles = useStyles()
	const { userProfile } = useCurrentUserProfile()
	const { register, getValues, control, formState: { isValid }, watch } = useForm({ mode: 'onChange' })
	const { setDialogState } = useContext(CompleteProfileDialogContext)
	const selectedCountry: Country = watch('country')
	const countries: Country[] = useMemo(() => Object.keys(sourceCountries).map((id) => {
		const countryId = id as keyof typeof sourceCountries
		return {
			...sourceCountries[countryId],
			id: countryId,
		}
	}), [])
	const [loading, setLoading] = useState<boolean>(false)

	const phoneNumberPlaceholder = useMemo(() => {
		return formatPhoneNumber(PhoneNumber.getExample(selectedCountry ? selectedCountry.id : 'US').getNumber())
	}, [selectedCountry])

	// event handlers
	const handleBackButtonClick = useCallback(() => {
		setDialogState('chooseUserType')
	}, [setDialogState])

	const handleFormSubmit = useCallback(() => {
		if (userProfile) {
			const formData = getValues()
			const student: Student = {
				displayName: userProfile.displayName,
				emailAddress: userProfile.emailAddress,
				phoneNumber: formData.phoneNumber,
				schoolName: formData.schoolName,
				grade: formData.grade.id,
				country: formData.country.id,
				PreferredTutorIDs: [],
			}
			setLoading(true)
			createStudent(student).then(() => {
				setLoading(false)
				setDialogState('success')
			})
		}
	}, [getValues, userProfile, setDialogState, setLoading])

	return (
		<>
			<DialogHeader title="Create Profile" />
			<DialogBody>
				<FormRow label="What country do you live in?">
					<Controller
						name="country"
						control={control}
						defaultValue={undefined}
						rules={{ required: true }}
						render={({ field }) => (
							<Select
								{...field}
								items={countries}
								options={{
									nameKey: 'name',
									idKey: 'id',
									searchableFields: ['name'],
								}}
								placeholder="Choose a Country..."
							/>
						)}
					/>
				</FormRow>
				<FormRow label="What's your phone number?">
					<Controller
						name="phoneNumber"
						control={control}
						defaultValue={undefined}
						rules={{ required: true }}
						render={({ field }) => (
							<PhoneInput
								{...field}
								defaultCountry={selectedCountry?.id}
								placeholder={phoneNumberPlaceholder}
							/>
						)}
					/>
				</FormRow>
				<FormRow label="What school do you go to?">
					<input placeholder="Shermer High School" {...register('schoolName', { required: true })} />
				</FormRow>
				<FormRow label="What grade are you in?">
				<Controller
						name="grade"
						control={control}
						defaultValue={undefined}
						rules={{ required: true }}
						render={({ field }) => (
							<Select
								{...field}
								items={grades}
								options={{
									nameKey: 'label',
									idKey: 'id',
									searchableFields: ['label'],
								}}
								placeholder="Choose a Grade..."
							/>
						)}
					/>
				</FormRow>
			</DialogBody>
			<DialogFooter>
				<DialogFooterButton color={Colors.border} textColor={Colors.graphite} onClick={handleBackButtonClick}>
					Back
				</DialogFooterButton>
				<DialogFooterButton color={blue.primary} textColor="white" disabled={!isValid} onClick={handleFormSubmit}>
					Next
				</DialogFooterButton>
			</DialogFooter>
			<LoadingOverlay loading={loading} />
		</>
	)
}

const useStyles = createUseStyles({
	provideStudentDetails: {
		display: 'grid'
	}
})